import { createReducer, on } from '@ngrx/store';
import {
  ApiKeyApifonChange,
  ApifonSubmitData,
  CloseApifonEditIntegration,
  ConfigurationTypeApifonChange,
  EditApifonIntegrationSystem,
  LookBackDaysApifonChange,
  NameApifonChange,
  SaveApifonConfigurationFailed,
  SaveApifonConfigurationSuccess,
  SmsSenderApifonChange,
  StatusApifonChange,
  ToggleCampaignReportingHelpTextStatus,
  ToggleCredentialsConfHelpTextStatus,
  ToggleIntegrationConfHelpTextStatus,
  TokenApifonChange,
  UpdateApifonConfigurationSuccess,
  ViberSenderApifonChange,
} from '../actions/apifon-configuration.actions';
import {
  CreateNewIntegration,
  SelectedIntegrationTypeChanged,
} from '../actions/integration-management-create-new.actions';
import { getShowUpdateBtn } from '../selectors/enbox-configuration.selectors';
import { getIsSelectedConfig } from '../selectors/apifon-configuration.selectors';
import { IntegrationType } from '../models/create-new-integration.model';

export interface ApifonConfigurationState {
  configurationTypeApifon: string;
  nameApifon: string;
  statusApifon: boolean;
  smsSenderApifon: string;
  viberSenderApifon: string;
  apiKeyApifon: string;
  tokenApifon: string;
  isSelectedConfig: boolean;
  isSelectedConfigViberSms: boolean;
  showUpdateBtn: boolean;
  disableName: boolean;
  integrationConfigurationHelpTextStatus: boolean;
  credentialsConfigurationHelpTextStatus: boolean;
  campaignReportingHelpTextStatus: boolean;
  lookBackDaysApifon: number | null;
  selectedApifonDataFromGrid: any;
  showCancelBtn: boolean;
  isSubmittedData: boolean;
}

const initialState: ApifonConfigurationState = {
  configurationTypeApifon: '',
  nameApifon: '',
  statusApifon: false,
  smsSenderApifon: '',
  viberSenderApifon: '',
  apiKeyApifon: '',
  tokenApifon: '',
  isSelectedConfig: false,
  isSelectedConfigViberSms: false,
  showUpdateBtn: false,
  disableName: false,
  integrationConfigurationHelpTextStatus: false,
  credentialsConfigurationHelpTextStatus: false,
  campaignReportingHelpTextStatus: false,
  lookBackDaysApifon: 0,
  selectedApifonDataFromGrid: [],
  showCancelBtn: false,
  isSubmittedData: false,
};

export const ApifonConfigurationReducer = createReducer(
  initialState,
  on(ConfigurationTypeApifonChange, (state, { configurationTypeApifon }) => ({
    ...state,
    configurationTypeApifon,
    isSelectedConfig: configurationTypeApifon.length > 0 ? true : false,
    isSelectedConfigViberSms: configurationTypeApifon === IntegrationType.APIFON_VIBER_SMS ? true : false,
  })),
  on(NameApifonChange, (state, { nameApifon }) => ({
    ...state,
    nameApifon,
  })),
  on(StatusApifonChange, (state, { statusApifon }) => ({
    ...state,
    statusApifon,
  })),
  on(SmsSenderApifonChange, (state, { smsSenderApifon }) => ({
    ...state,
    smsSenderApifon,
  })),
  on(ViberSenderApifonChange, (state, { viberSenderApifon }) => ({
    ...state,
    viberSenderApifon,
  })),
  on(ApiKeyApifonChange, (state, { apiKeyApifon }) => ({
    ...state,
    apiKeyApifon,
  })),
  on(TokenApifonChange, (state, { tokenApifon }) => ({
    ...state,
    tokenApifon,
  })),
  on(CreateNewIntegration, () => ({
    ...initialState,
  })),
  on(SaveApifonConfigurationSuccess, (state, { res }) => ({
    ...state,
    showUpdateBtn: true,
    disableName: true,
    nameApifon: res.payload.data.integrationConfig.integrationDisplayName,
    statusApifon: res.payload.data.deleted,
    configurationTypeApifon: res.payload.data.integrationConfig.integrationSystem,
    isSelectedConfigViberSms:
      res.payload.data.integrationConfig.integrationSystem === IntegrationType.APIFON_VIBER_SMS ? true : false,
    smsSenderApifon:
      res.payload.data.integrationConfig.integrationSystem === IntegrationType.APIFON_VIBER_SMS
        ? res.payload.data.integrationConfig.smsSender
        : res.payload.data.integrationConfig.sender,
    viberSenderApifon: state.isSelectedConfigViberSms
      ? res.payload.data.integrationConfig.viberSender
      : state.viberSenderApifon,
    apiKeyApifon: res.payload.data.endpointConfiguration.credentialConfig.headers.API_KEY,
    tokenApifon: res.payload.data.endpointConfiguration.credentialConfig.headers.API_TOKEN,
    lookBackDaysApifon: res.payload.data.integrationConfig.reportingConfiguration.lookbackDays,
    integrationConfigurationHelpTextStatus: false,
    credentialsConfigurationHelpTextStatus: false,
    campaignReportingHelpTextStatus: false,
    isSubmittedData: false,
  })),
  on(SaveApifonConfigurationFailed, (state) => ({
    ...state,
    showUpdateBtn: false,
    disableName: false,
  })),
  on(EditApifonIntegrationSystem, (state, { apifonData }) => ({
    ...state,
    isSelectedConfig: true,
    showUpdateBtn: true,
    disableName: true,
    nameApifon: apifonData.integrationConfig.integrationDisplayName,
    statusApifon: apifonData.deleted,
    configurationTypeApifon: apifonData.integrationConfig.integrationSystem,
    isSelectedConfigViberSms:
      apifonData.integrationConfig.integrationSystem === IntegrationType.APIFON_VIBER_SMS ? true : false,
    smsSenderApifon:
      apifonData.integrationConfig.integrationSystem === IntegrationType.APIFON_VIBER_SMS
        ? apifonData.integrationConfig.smsSender
        : apifonData.integrationConfig.sender,
    viberSenderApifon:
      apifonData.integrationConfig.integrationSystem === IntegrationType.APIFON_VIBER_SMS
        ? apifonData.integrationConfig.viberSender
        : state.viberSenderApifon,
    apiKeyApifon: apifonData.endpointConfiguration.credentialConfig.headers.API_KEY,
    tokenApifon: apifonData.endpointConfiguration.credentialConfig.headers.API_TOKEN,
    lookBackDaysApifon: apifonData.integrationConfig.reportingConfiguration.lookbackDays,
    showCancelBtn: true,
    isSubmittedData: false,
  })),
  on(UpdateApifonConfigurationSuccess, (state, { res }) => ({
    ...state,
    showUpdateBtn: true,
    disableName: true,
    nameApifon: res.payload.data.integrationConfig.integrationDisplayName,
    statusApifon: res.payload.data.deleted,
    configurationTypeApifon: res.payload.data.integrationConfig.integrationSystem,
    isSelectedConfigViberSms:
      res.payload.data.integrationConfig.integrationSystem === IntegrationType.APIFON_VIBER_SMS ? true : false,
    smsSenderApifon:
      res.payload.data.integrationConfig.integrationSystem === IntegrationType.APIFON_VIBER_SMS
        ? res.payload.data.integrationConfig.smsSender
        : res.payload.data.integrationConfig.sender,
    viberSenderApifon: state.isSelectedConfigViberSms
      ? res.payload.data.integrationConfig.viberSender
      : state.viberSenderApifon,
    apiKeyApifon: res.payload.data.endpointConfiguration.credentialConfig.headers.API_KEY,
    tokenApifon: res.payload.data.endpointConfiguration.credentialConfig.headers.API_TOKEN,
    lookBackDaysApifon: res.payload.data.integrationConfig.reportingConfiguration.lookbackDays,
  })),
  on(ToggleIntegrationConfHelpTextStatus, (state) => ({
    ...state,
    integrationConfigurationHelpTextStatus: !state.integrationConfigurationHelpTextStatus,
  })),
  on(ToggleCredentialsConfHelpTextStatus, (state) => ({
    ...state,
    credentialsConfigurationHelpTextStatus: !state.credentialsConfigurationHelpTextStatus,
  })),
  on(LookBackDaysApifonChange, (state, { lookBackDaysApifon }) => ({
    ...state,
    lookBackDaysApifon,
  })),
  on(ToggleCampaignReportingHelpTextStatus, (state) => ({
    ...state,
    campaignReportingHelpTextStatus: !state.campaignReportingHelpTextStatus,
  })),
  on(CloseApifonEditIntegration, (state) => initialState),
  on(ApifonSubmitData, (state) => ({
    ...state,
    isSubmittedData: true,
  })),
  on(SelectedIntegrationTypeChanged, (state, { selectedIntegrationType }) => ({
    ...state,
    ...state,
    configurationTypeApifon:
      selectedIntegrationType === IntegrationType.APIFON_VIBER_SMS ||
      selectedIntegrationType === IntegrationType.APIFON_SMS
        ? selectedIntegrationType
        : '',
    isSelectedConfig:
      selectedIntegrationType === IntegrationType.APIFON_VIBER_SMS ||
      selectedIntegrationType === IntegrationType.APIFON_SMS
        ? true
        : false,
    isSelectedConfigViberSms: selectedIntegrationType === IntegrationType.APIFON_VIBER_SMS ? true : false,
  })),
);
